import React, { useState, useEffect } from "react";
import styled from "styled-components";
import HeightInterval from "../Components/HeightInterval.jsx";
import config from "../config.js";
import { getSignupAttributionCategory } from "../utils/attribution.js";

const SERVER_URI = config.serverUri;

const Info = ({ onKeywordClick }) => {
  const [keywords, setKeywords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Flutter 환경 감지
  const isFlutterApp = React.useMemo(() => {
    return !!(window.flutter_inappwebview && window.flutter_inappwebview.callHandler) ||
           !!(window.LawlowApp && typeof window.LawlowApp.postMessage === 'function');
  }, []);

  // 베스트 5개 테마 키워드 API 호출
  useEffect(() => {
    const fetchBestKeywords = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        const category = getSignupAttributionCategory();

        if (category) {
          params.set("category", category);
        }

        const queryString = params.toString();
        const url = `${SERVER_URI}/best-keywords${queryString ? `?${queryString}` : ""}`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setKeywords(data.keywords || []);
        } else {
          console.error('베스트 키워드 API 호출 실패:', response.status);
          // 실패 시 기본 키워드 사용 (새로운 구조에 맞게)
          setKeywords([
            {
              keyword: "지적재산권",
              example: "저작권 침해 관련해서 법적 대응을 어떻게 해야 할까요?"
            },
            {
              keyword: "폭행 및 절도", 
              example: "폭행 사건이 발생했는데 경찰에 신고하는 방법이 궁금합니다."
            },
            {
              keyword: "노동 및 고용",
              example: "회사에서 부당해고를 당했는데 어떻게 대응해야 할까요?"
            },
            {
              keyword: "금융 및 투자",
              example: "투자 손실에 대한 법적 구제 방법이 있을까요?"
            },
            {
              keyword: "교통 사고 및 위반",
              example: "교통사고 발생 시 보험 처리와 법적 절차가 궁금합니다."
            }
          ]);
        }
      } catch (error) {
        console.error('베스트 키워드 API 에러:', error);
        // 에러 시 기본 키워드 사용 (새로운 구조에 맞게)
        setKeywords([
          {
            keyword: "금융 및 투자",
            example: "투자 손실에 대한 법적 구제 방법이 있을까요?"
          },
          {
            keyword: "폭행 및 절도", 
            example: "폭행 사건이 발생했는데 경찰에 신고하는 방법이 궁금합니다."
          },
          {
            keyword: "노동 및 고용",
            example: "회사에서 부당해고를 당했는데 어떻게 대응해야 할까요?"
          },
          {
            keyword: "교통 사고 및 위반",
            example: "교통사고 발생 시 보험 처리와 법적 절차가 궁금합니다."
          },
          {
            keyword: "지적재산권",
            example: "저작권 침해 관련해서 법적 대응을 어떻게 해야 할까요?"
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBestKeywords();
  }, []);

  return (
    <Wrapper>
      <UpperText>
        비슷한 사건에서 이긴 근거,
        <br />
        내 상황에 맞춰 찾아보세요.
      </UpperText>
      <ResponsiveHeight />
      <SubtitleContainer>
        <StrokeLine />
        <LowerText>최근 사람들이 많이 검색한 주제</LowerText>
        <StrokeLine />
      </SubtitleContainer>
      <HeightInterval height={16} />
      <TagContainer>
        {isLoading ? (
          // 로딩 중일 때 스켈레톤 UI 또는 기본 태그 표시
          <>
            <SkeletonTag />
            <SkeletonTag />
            <SkeletonTag />
            <SkeletonTag />
            <SkeletonTag />
          </>
        ) : (
          // API에서 받아온 키워드를 동적으로 렌더링
          keywords.map((item, index) => (
            <TopicTag
              key={index}
              $bgColor={index % 2 === 0 ? "rgba(0, 243, 112, 0.07)" : "rgba(0, 87, 255, 0.06)"}
              onClick={() => onKeywordClick && onKeywordClick(item.example)}
            >
              {item.keyword}
            </TopicTag>
          ))
        )}
      </TagContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  max-width: calc(100vw - 48px);
  @media (max-width: 10000px) and (min-width: 1141px) {
    width: 680px;
  }

  @media not all and (min-width: 1141px) {
    @media (min-width: 600px) {
      width: min(680px, calc(100vw - 64px));
    }
  }

  @media not all and (min-width: 600px) {
    width: calc(100% - 48px);
  }
`;

const UpperText = styled.div`
  color: #000;
  text-align: center;
  font-family: Pretendard;
  font-style: normal;
  font-weight: 600;
  font-size: 32px;
  line-height: 48px;
  letter-spacing: -1.28px;

  @media not all and (min-width: 600px) {
    font-size: 26px;
    line-height: 40px;
    letter-spacing: -1.3px;
  }
`;

const ResponsiveHeight = styled.div`
  height: 32px;
  
  @media not all and (min-width: 600px) {
    height: 56px;
  }
`;

const SubtitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
`;

const StrokeLine = styled.hr`
  position: relative;
  width: 239px;
  max-width: 239px;
  min-width: 0;
  height: 0px;
  flex: 1 1 0;
  border: 1px solid #f1f1f1;
  margin: 0 0 0 1px;

  @media not all and (min-width: 600px) {
    width: auto;
  }
`;

const LowerText = styled.div`
  color: rgba(0, 0, 0, 0.30);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-align: center;
  white-space: nowrap;

  @media not all and (min-width: 600px) {
    font-size: 12px;
  }
`;

const TagContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const TopicTag = styled.div`
  display: flex;
  width: auto;
  height: 42px;
  padding: 8px 24px;
  justify-content: center;
  align-items: center;
  border-radius: 24px;
  background: ${props => props.$bgColor};
  color: rgba(0, 0, 0, 0.60);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  letter-spacing: -0.28px;
  white-space: nowrap;
  box-sizing: border-box; /* padding을 포함한 전체 높이 42px 설정을 위함*/
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  @media not all and (min-width: 600px) {
    padding: 6px 16px;
    height: 38px; 
    font-size: 12px;
    letter-spacing: -0.24px;
  }
`;

const SkeletonTag = styled.div`
  display: flex;
  width: 120px;
  height: 42px;
  border-radius: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
  
  @media not all and (min-width: 600px) {
    height: 38px;
    width: 100px;
  }
`;

export default Info;
